import Immutable from 'immutable';
import React from 'react';
import styled from 'styled-components';
import RenderContext from '../rendercontext';
import {defaultTheme} from '../../../data/themes';
import Error from '../../forms/error';
import Icon from '../../icon';
import { getErrorType } from '../../../data/validation';
import { ErrorList } from '../../../data/validation';
import { isDeleted } from '../misc/definitionutils';
import { getPhrase } from '../../../data/phrases';
import { addRowToIndex, applyFilter, clearFilter, createTabularInputIndex, deleteRowFromIndex, getEmptyIndex, getSortIndex, goToNextIndexPage, orderBy, setFilterValue, tabularIndexHasNextPage, toggleFilterVisible, updateDefaultFilter } from './tabularinputindex';
import { RowCountMessage, Pager } from '../../data/pager';
import { hidePendingRequest, showPendingRequest } from '../../../actions/modal';

import TextInput from '../../forms/textinput';
import LookupInput from '../../forms/lookupinput';
import SearchDateInput from '../../forms/searchdateinput';
import SearchNumberInput from '../../forms/searchnumberinput';
import SearchRuntimeInput from '../../forms/searchruntimeinput';
import { getComponentPreferenceKey, getPreference, setPreference } from '../../../data/preferences';

const EMPTY_LIST = Immutable.List([]);
const EMPTY_MAP = Immutable.Map({});

let sequence = 1;


const INDEX_PATH_BASE = Object.freeze(['tmp', 'tabInpIdx']);
const TAB_INDEX_PROPERTY = '__tabIndexPath';

export function getTabularInputIndexPathBase() {
	return INDEX_PATH_BASE;
}

export default function TabularInputDefinition() {
	if (!(this instanceof TabularInputDefinition)) {
		return new TabularInputDefinition();
	}

	const me = this;
	let keySuffixCallback;
	let rowValidator;
	let rowListValidator;
	let items;
	let path = [];
	let isHidden;
	let isVisibleCallback;
	let entityName;
	let rowChangeListener;
	let tableChangeListener;
	let marginTop;
	let margin;
	let noHeader;
	let mainHeader;
	let mainHeaderCallback;
	let deletable = true;
	let deletableCallback;
	let noCreate;
	let noCreateCallback;
	let withRowCountMessage;
	let maxRowsPerPage;
	let readOnly;
	let readOnlyCallback;
	let onMount;
	let defaultFilterFactory;
	let indexTimestampCallback;
	let rowClassCallback;
	let recreateOnMount;

	this.setKeySuffix = function(value) {
		if (typeof value === 'function') {
			keySuffixCallback = value;
		}
		return this;
	};

	this.setRecreateOnMount = function(value) {
		recreateOnMount = value;
		return this;
	};

	this.setIndexTimestamp = function(value) {
		indexTimestampCallback = value;
		return this;
	};

	this.setRowClass = function(clb) {
		rowClassCallback = clb;
		return this;
	};

	const getIndexTimestamp = function(ctx) {
		if (indexTimestampCallback) {
			return indexTimestampCallback(ctx);
		} else {
			return 0;
		}
	}

	this.onMount = function(value) {
		onMount = value;
		return this;
	};

	this.setDefaultFilterFactory = function(value) {
		defaultFilterFactory = value;
		return this;
	};

	const getDefaultFilter = function(ctx) {
		return defaultFilterFactory ? defaultFilterFactory(ctx) : null;
	};

	this.setReadOnly = function(value) {
		if (typeof value === 'function') {
			readOnlyCallback = value;
			readOnly = false;
		} else {
			readOnlyCallback = null;
			readOnly = value;
		}
		return this;
	};

	this.isReadOnly = function(ctx) {
		if (readOnlyCallback) {
			return readOnlyCallback(ctx);
		} else {
			return readOnly;
		}
	};

	this.recreateIndex = function(ctx) {
		const index = createTabularInputIndex(ctx, ctx.getData() || EMPTY_LIST, {
			maxRowsPerPage: getMaxRowsPerPagePreference(ctx.getFullPath()) || maxRowsPerPage,
			withNewRow: !this.isReadOnly(ctx) && !this.getNoCreate(ctx),
			defaultFilter: getDefaultFilter(ctx)
		});
		//console.log('createdIndex', index.toJS());
		ctx.setRootProperty(INDEX_PATH_BASE.concat(ctx.getFullPath()), index);
	};

	this.setWithRowCountMessage = function(value) {
		withRowCountMessage = value;
		return this;
	};

	this.setMaxRowsPerPage = function(value, overridePreference) {
		maxRowsPerPage = value;
		return this;
	};

	this.setMainHeader = function(value) {
		if (typeof value === 'function') {
			mainHeader = null;
			mainHeaderCallback = value;
		} else {
			mainHeaderCallback = null;
			mainHeader = value;
		}
		return this;
	};

	this.setNoCreate = function(value) {
		if (typeof value === 'function') {
			noCreate = null;
			noCreateCallback = value;
		} else {
			noCreateCallback = null;
			noCreate = value;
		}
		return this;
	};

	this.getNoCreate = function(ctx) {
		if (noCreateCallback) {
			return noCreateCallback(ctx);
		} else {
			return noCreate;
		}
	};

	this.getMainHeader = function(ctx) {
		if (mainHeaderCallback) {
			return mainHeaderCallback(ctx);
		} else {
			return mainHeader;
		}
	};

	this.setDeletable = function(value) {
		if (typeof value === 'function') {
			deletableCallback = value;
			deletable = null;
		} else {
			deletableCallback = null;
			deletable = value;
		}
		return this;
	};

	const isDeletable = function(ctx) {
		if (deletableCallback) {
			return deletableCallback(ctx);
		} else {
			return deletable;
		}
	};

	this.setMargin = function(value) {
		margin = value;
		return this;
	};

	this.setMarginTop = function(value) {
		marginTop = value;
		return this;
	};

	this.setNoHeader = function(value) {
		noHeader = value;
		return this;
	};

	const itemChangeListener = function(ctx, item) {
		const rowIndex = ctx.getBasePathLeaf();
		if (rowChangeListener) {
/*			if (ctx._newRow) {
				ctx.updateData(true);
			}*/
			rowChangeListener(
				ctx,
				rowIndex,
				item
			);
		}
		let parentCtx;
		if (tableChangeListener) {
			parentCtx = ctx.withShortenedBasePath(1);
			tableChangeListener(parentCtx, rowIndex, item);
		}
		if (ctx._newRow) {
			if (!parentCtx) {
				parentCtx = ctx.withShortenedBasePath(1);
			}
			me.setIndex(ctx, addRowToIndex(me.getIndex(ctx), parentCtx.getData().size));
		}
	};

	this.onRowChange = function(value) {
		rowChangeListener = value;
		return this;
	};

	this.onChange = function(value) {
		tableChangeListener = value;
		return this;
	}


	this.setEntityName = function(value) {
		entityName = value;
		return this;
	};

	this.getEntityName = function() {
		return entityName;
	}

	this.setItems = function(value) {
		items = Immutable.List(value);
		items.forEach(item => {
			item._parentDefinition = this;
			item._dontCacheErrorPath = true;
			if (item.onChange) {
				item.onChange(itemChangeListener);
			}
		});
		return this;
	}

	this.getItems = function() {
		return items;
	}

	this.setRowValidator = function(value) {
		if (value && !value.isContextValidator) {
			throw 'Only context validator allowed in TabularInputDefinition';
		}
		rowValidator = value;
		return this;
	};

	this.setRowListValidator = function(value) {
		if (value && !value.isContextValidator) {
			throw 'Only context validator allowed in TabularInputDefinition';
		}
		rowListValidator = value;
		return this;
	};

	this.setPath = function(value) {
		if (value === null || value === undefined) {
			value = [];
		} else if (!Array.isArray(value)) {
			value = [value];
		}
		path = value;
		return this;
	};


	this.getPath = function() {
		return path;
	};

	this.setVisible = function(value) {
		if (typeof value === 'function') {
			isHidden = false;
			isVisibleCallback = value;
		} else {
			isHidden = !value;
			isVisibleCallback = null;
		}
		return this;
	};

	const validateRow = function(errors, ctx, itemName) {
		if (rowValidator) {
			rowValidator.validate(errors, ctx, itemName);
		}
	};

	const validateRowList = function(errors, ctx, itemName) {
		if (rowListValidator) {
			rowListValidator.validate(errors, ctx, itemName);
		}
	};

	const validateAllInItems = function(ctx, errors, rowIndex) {
		ctx = ctx.withAppendedBasePath(rowIndex);
		if (isDeleted(ctx.getData())) {
			return;
		}
		validateRow(errors, ctx);
		items.forEach(item => {
			item.validateAll(ctx, errors);
		});
	};

	this.validateAll = function(ctx, errors) {
		ctx = ctx.withAppendedBasePath(path);
		if (isHidden || (isVisibleCallback && !isVisibleCallback(ctx))) {
			return null;
		}
		validateRowList(errors, ctx);
		//validate(errors, ctx);
		(ctx.getData() || EMPTY_LIST).forEach((row, index) => {
			validateAllInItems(ctx, errors, index);
		});
	};

	this.validateOnChange = function(ctx, errors, itemName) {
		validateRow(errors, ctx, itemName);
		ctx = ctx.withShortenedBasePath(1);
		validateRowList(errors, ctx, itemName);
		if (this._parentDefinition && this._parentDefinition.validateOnChange) {
			this._parentDefinition.validateOnChange(ctx.withShortenedBasePath(path.length), errors, itemName)
		}
		return errors;
	};

	const runChangeValidation = function(ctx, itemName) {
		let errors = new ErrorList();
		me.validateOnChange(ctx, errors, itemName);
		ctx.setRootErrors(errors.mergeWith(ctx.getRootErrors()));
	};

	this.getIndex = function(ctx) {
		return ctx.getRootProperty(ctx.getCalcProperty(TAB_INDEX_PROPERTY));
	};

	this.setIndex = function(ctx, index) {
		ctx.setRootProperty(ctx.getCalcProperty(TAB_INDEX_PROPERTY), index);
	};

	this.orderBy = function(ctx, itemIndex) {
		const rows = ctx.getData();
		if (!rows) {
			return;
		}
		const item = items.get(itemIndex);
		const prHandle = showPendingRequest(getPhrase('orderingRows'));
		window.setTimeout(async () => {
			let index = this.getIndex(ctx);
			let currItemIndex = index.get('sortItemIndex');
			const sortIndex = await getSortIndex(ctx, index, item, itemIndex, rows);
			index = await orderBy(index, sortIndex, rows.size, currItemIndex == itemIndex);
			index = index.set('sortItemIndex', currItemIndex == itemIndex ? ('desc.' + itemIndex) : ('' + itemIndex));
			this.setIndex(ctx, index);
			hidePendingRequest(prHandle);
		}, 0);
	};

	const _onMount = function(ctx) {
		if (onMount) {
			onMount(ctx, me);
		}
	};

	this.render = function(ctx, key = 0) {
		const indexTimestamp = getIndexTimestamp(ctx);
		ctx = ctx.withAppendedBasePath(path);
		if (isHidden || (isVisibleCallback && !isVisibleCallback(ctx))) {
			return null;
		}
		ctx.setReadOnly(ctx.isContextReadOnly() || this.isReadOnly(ctx));
		const indexPath = INDEX_PATH_BASE.concat(ctx.getFullPath());
		let calcProps = {};
		calcProps[TAB_INDEX_PROPERTY] = indexPath;
		ctx.mergeCalcProperties(calcProps);
		if (!ctx.getData()) {
			const fullPath = ctx.getBasePath();
			ctx.setOnBeforeSetProperty(rootData => {
				if (rootData.getIn(fullPath)) {
					return rootData;
				} else {
					return rootData.setIn(fullPath, EMPTY_LIST);
				}
			});
		}
		if (keySuffixCallback) {
			key += '_' + keySuffixCallback(ctx);
		}
		return <TabularInput
			key={key}
			items={items}
			ctx={ctx}
			defaultFilter={getDefaultFilter(ctx)}
			indexTimestamp={indexTimestamp}
			marginTop={marginTop}
			margin={margin}
			recreateOnMount={recreateOnMount}
			withRowCountMessage={withRowCountMessage}
			maxRowsPerPage={maxRowsPerPage}
			mainHeader={this.getMainHeader(ctx)}
			rows={ctx.getData() || EMPTY_LIST}
			indexPath={indexPath}
			onMount={_onMount}
			index={this.getIndex(ctx)}
			errors={ctx.getErrors()}
			noHeader={noHeader}
			noCreate={this.getNoCreate(ctx)}
			rowClassCallback={rowClassCallback}
			onSortRequested={itemIndex => {
				this.orderBy(ctx, itemIndex);
			}}
			onRowDeleted={rowCtx => {
				if (tableChangeListener) {
					tableChangeListener(rowCtx.withShortenedBasePath(1), rowCtx.getBasePathLeaf());
				}
				runChangeValidation(rowCtx, '_deleted');
				this.setIndex(ctx, deleteRowFromIndex(this.getIndex(ctx), rowCtx.getBasePathLeaf()));
			}}
			isDeletable={isDeletable}
			onApplyFilterRequested={() => {
				const prHandle = showPendingRequest(getPhrase('filteringRows'));
				window.setTimeout(async () => {
					const index = await applyFilter(ctx, this.getIndex(ctx), items, ctx.getData());
					hidePendingRequest(prHandle);
					this.setIndex(ctx, index);

				})
			}}
		/>
	}
}




class Row extends React.Component {

	shouldComponentUpdate(nextProps) {
		return this.props.row !== nextProps.row || this.props.errors !== nextProps.errors;
	}

	render() {
		const ctx = this.props.ctx.withAppendedBasePath(this.props.index);
		ctx._newRow = this.props.isNew;
		const readOnly = ctx.isContextReadOnly();
		const items = this.props.items;
		const maxItemIndex = items.size - 1;
		let lastEditableIndex = -1;
		if (this.props.isLast) {
			for (let i = maxItemIndex; i >= 0; i--) {
				let item = items.get(i);
				if (item.isFocusable && item.isReadOnly && item.isFocusable() && !item.isReadOnly(ctx)) {
					lastEditableIndex = i;
					break;
				}
			}
		}
		let customClass;
		if (this.props.rowClassCallback) {
			customClass = this.props.rowClassCallback(ctx);
		}
		return (
			<tr className={(readOnly ? 'readOnly ' : '') + (customClass ? customClass : '')} data-last={this.props.isLast ? 1 : null}>
				{
					items.map((item, index) => {
						const error = item.getNoError() ? null : item.getError(ctx);
						let errorType = getErrorType(error);
						return (
							// todo: mela by se brat v potaz i readonlyovost (jinak se na posledni nedostane), ale zatim to takhle staci
							<td key={index} data-error={errorType} data-last={index === lastEditableIndex ? 1 : null}>
								{
									item.renderInput(ctx)
								}
								<Error
									error={error}
								/>
							</td>
						);
					})
				}
				<td className="icon">
					{
						this.props.row !== EMPTY_MAP ? (
							<Icon
								name="delete"
								active
								disabled={readOnly || !this.props.isDeletable(ctx)}
								hint={getPhrase('deleteRecord')}
								onMouseDown={e => {
									e.preventDefault();
									ctx.setData(this.props.row.set('_deleted', true));
									this.props.onRowDeleted(ctx);
								}}
							/>
						) : (
							<Icon
								name="asterix"
								hint={getPhrase('newRecord')}
							/>
						)
					}
				</td>
			</tr>
		);
	}
}




class Header extends React.Component {
	constructor() {
		super();
		this.toggleFilterVisible = this.toggleFilterVisible.bind(this);
		this.clearFilter = this.clearFilter.bind(this);
	}

	clearFilter() {
		this.setIndex(clearFilter(this.props.index));
		this.props.onApplyFilterRequested();
	}

	toggleFilterVisible() {
		this.setIndex(toggleFilterVisible(this.props.index));
		this.props.onApplyFilterRequested();
	}

	shouldComponentUpdate(nextProps) {
		return this.props.items !== nextProps.items || this.props.index !== nextProps.index;
	}

	setIndex(index) {
		this.props.ctx.setRootProperty(this.props.indexPath, index);
	}

	setFilterValue(itemIndex, value) {
		const newIndex = setFilterValue(this.props.index, itemIndex, value);
		if (newIndex !== this.props.index) {
			this.setIndex(newIndex);
			this.props.onApplyFilterRequested();
		}
	}

	renderFilterInput(item, itemIndex) {
		if (!item.getFilterType) {
			return null;
		}
		const filterType = item.getFilterType();
		if (!filterType) {
			return null;
		}
		const value = this.props.index.getIn(['filter', itemIndex]);
		if (filterType === 'text') {
			return <TextInput
				value={value || ''}
				onChange={value => {
					this.setFilterValue(itemIndex, value);
				}}
				changeOnBlur
			/>;
		} else if (filterType === 'lookup') {
			const lookup = item.getIndexLookup(this.props.ctx);
			if (!lookup) {
				return null;
			}
			return <LookupInput
				value={value || ''}
				onChange={value => {
					this.setFilterValue(itemIndex, value);
				}}
				getRows={lookup.getRows}
				getText={lookup.getText}
				combo={lookup.supportsCombo}
			/>;
		} else if (filterType === 'searchDate') {
			return <SearchDateInput
				value={value || ''}
				onChange={value => {
					this.setFilterValue(itemIndex, value);
				}}
			/>;
		} else if (filterType === 'searchNumber') {
			return <SearchNumberInput
				value={value || ''}
				onChange={value => {
					this.setFilterValue(itemIndex, value);
				}}
			/>;
		} else if (filterType === 'searchRuntime') {
			return <SearchRuntimeInput
				value={value || ''}
				onChange={value => {
					this.setFilterValue(itemIndex, value);
				}}
			/>;
		} else {
			return filterType;
		}
	}

	render() {
		const ctx = this.props.ctx;
		const index = this.props.index;
		let sortIndex = -1;
		let sortDesc = false;
		if (index) {
			const sortIndexStr = index.get('sortItemIndex');
			if (typeof sortIndexStr === 'string' && sortIndexStr.startsWith('desc.')) {
				sortDesc = true;
				sortIndex = parseInt(sortIndexStr.substring(5));
			} else {
				sortIndex = parseInt(sortIndexStr);
			}
		}
		const withFilter = !!this.props.index && this.props.index.get('filterVisible');
		return (
			<HeaderWrapper>
				{
					this.props.mainHeader && (
						<tr>
							<td colSpan={this.props.items.size + 1}>
								{this.props.mainHeader}
							</td>
						</tr>
					)
				}
				<tr>
					{
						this.props.items.map((item, index) => {
							const type = item.getType && item.getType();
							const sortable = type && type !== 'entityLink' && type !== 'custom';
							return (
								<td
									key={index}
									className={sortable ? 'sortable' : ''}
									onMouseDown={e => {
										if (sortable) {
											e.target.focus();
											e.preventDefault();
											this.props.onSortRequested(index);
										}
									}}
								>
									{item.getLabel(ctx)}
									{
										index === sortIndex && (
											<Icon
												className="sort"
												name={sortDesc ? 'sortDesc' : 'sortAsc'}
											/>
										)
									}
								</td>
							);
						})
					}
					<td>
						<Icon
							name="filter"
							active
							onMouseDown={this.toggleFilterVisible}
						/>
					</td>
				</tr>
				{
					withFilter && (
						<tr>
							{
								this.props.items.map((item, index) => {
									return (
										<td key={index} className="filter">
											{
												this.renderFilterInput(item, index)
											}
										</td>
									)
								})
							}
							<td>
								<Icon
									name="eraser"
									active
									hint={getPhrase('clearFilter')}
									onMouseDown={this.clearFilter}
								/>
							</td>
						</tr>
					)
				}
			</HeaderWrapper>
		);
	}
}

const HeaderWrapper = styled.thead`
	.sortable {
		cursor: pointer;
		&:hover {
			text-decoration: underline;
		}
		.icon.sort {
			margin-left: 4px;
			opacity: 0.7;
		}
	}

	td.filter {
		padding: 0px;
		.ps-input {
			width: 100%;
		}
	}
`;


const Table = styled.table`
	width: 100%;
	border-spacing: 0px;
	border-collapse: collapse;

	thead {
		td {
			background: ${props => props.theme.tabularInputHeaderBackground || defaultTheme.tabularInputHeaderBackground};
			padding: ${props => props.theme.tabularInputHeaderCellPadding || defaultTheme.tabularInputHeaderCellPadding};
			&:not(:first-child) {
				border-left: ${props => props.theme.tabularInputHeaderCellSeparatorBorder || defaultTheme.tabularInputHeaderCellSeparatorBorder};
			}
			border: ${props => props.theme.tabularInputHeaderCellBorder || defaultTheme.tabularInputHeaderCellBorder};
			text-align: center;
			color: ${props => props.theme.tabularInputHeaderColor || defaultTheme.tabularInputHeaderColor};
			font-size: ${props => props.theme.tableHeaderFontSize || defaultTheme.tableHeaderFontSize};
			font-weight: ${props => props.theme.tabularInputHeaderFontWeight || defaultTheme.tabularInputHeaderFontWeight};

		}
	}

	tbody {
		.ps-input {
			width: 100%;
		}

		.ps-button {
			margin: 2px;
		}

		tr:not(.readOnly) {
			&:hover {
				td {
					background: ${props => props.theme.tabularInputRowHoverBackground || defaultTheme.tabularInputRowHoverBackground};
				}
			}
		}
		td {
			vertical-align: top;
			text-align: center;
			padding: 0px;
			border: ${props => props.theme.tabularInputCellBorder || defaultTheme.tabularInputCellBorder};

			background: ${props => props.theme.tabularInputCellBackground || props.theme.inputBackground || defaultTheme.inputBackground};

			.ps-input {
				border-radius: 0px;
				border: none;
				&:not(.readonly) {
					background: transparent !important;
				}
				padding: ${props => props.theme.tabularInputInputPadding || defaultTheme.tabularInputInputPadding};

				&.comment, &.entityLink {
					position: relative;
					top: 2px;
				}
			}

			&.icon {
				padding: ${props => props.theme.tabularInputInputPadding || defaultTheme.tabularInputInputPadding};
				.icon {
					position: relative;
					top: -1px;

					&[data-name=asterix] {
						font-size: 10px;
						top: -2px;
					}
				}
			}

			input:not([readonly]) {
				background: transparent !important;
			}

			&[data-error=error] {
				> .ps-input {
					background: ${props => props.theme.inputErrorBackground ?? defaultTheme.inputErrorBackground} !important;
				}
			}
			&[data-error=warning] {
				> .ps-input {
					background: ${props => props.theme.inputWarningBackground ?? defaultTheme.inputWarningBackground} !important;
				}
			}
		}

	}
`;

const Container = styled.div`
	margin: ${props => props.margin ?? props.theme.formItemMargin ?? defaultTheme.formItemMargin};
	margin-top: ${props => props.marginTop};
`;

const Col = styled.col`
	min-width: ${props => props.minWidth};
`;

function getMaxRowsPerPagePreference(path) {
	return getPreference(getComponentPreferenceKey('tabularInput', path, 'maxRowsPerPage'));
}

function setMaxRowsPerPagePreference(path, value) {
	setPreference(getComponentPreferenceKey('tabularInput', path, 'maxRowsPerPage'), value);
}

class TabularInput extends React.Component {
	constructor() {
		super();
		this.ref = React.createRef();
		this.setPage = this.setPage.bind(this);
		this.onItemAction = this.onItemAction.bind(this);
	}

	onItemAction(e) {
		//e.preventDefault();
		const cell = e.target.closest('td');
		if (cell.getAttribute('data-last')) {
			const row = e.target.closest('tr');
			if (row.getAttribute('data-last')) {
				const index = this.props.index;
				if (tabularIndexHasNextPage(index)) {
					e.preventDefault();
					this.setIndex(goToNextIndexPage(index));
				}
			}
		}
	}

	shouldComponentUpdate(nextProps) {
		return this.props.rows !== nextProps.rows ||
			this.props.errors !== nextProps.errors ||
			this.props.index !== nextProps.index ||
			this.props.mainHeader !== nextProps.mainHeader ||
			this.props.indexTimestamp !== nextProps.indexTimestamp ||
			this.props.defaultFilter !== nextProps.defaultFilter;
	}

	componentDidMount() {
		this.ref.current.addEventListener('action', this.onItemAction);
		const ctx = this.props.ctx;
		if (!this.props.index || this.props.recreateOnMount) {
			const index = createTabularInputIndex(ctx, this.props.rows, {
				maxRowsPerPage: getMaxRowsPerPagePreference(ctx.getFullPath()) || this.props.maxRowsPerPage,
				withNewRow: !this.props.readOnly && !this.props.noCreate,
				defaultFilter: this.props.defaultFilter
			});
			//console.log('createdIndex', index.toJS());
			this.setIndex(index);
		}
		if (this.props.onMount) {
			this.props.onMount(ctx);
		}
	}

	getInputs() {
		const el = this.ref.current;
		if (!el) {
			return [];
		}
		return el.querySelectorAll('input:not([readonly]),textarea');
	}

	focus(index) {
		const inps = this.getInputs();
		if (!inps[index]) {
			return;
		}
		while (inps[index].readonly) {
			index++;
			if (!inps[index]) {
				return;
			}
		}
		const inp = inps[index];
		if (inp) {
			inp.focus();
			inp.select();
		}
	}

	componentDidUpdate(prevProps) {
		const index = this.props.index;
		const prevIndex = prevProps.index;
		if (prevIndex && index && index !== prevIndex) {
			const pager = index.get('pager');
			const prevPager = prevIndex.get('pager');
			if (pager !== prevPager) {
				if (pager.get('firstRow') !== prevPager.get('firstRow')) {
					window.setTimeout(() => {
						this.focus(0);
					}, 0);
				}
			}
		}
		if (this.props.defaultFilter !== prevProps.defaultFilter) {
			this.setIndex(updateDefaultFilter(this.props.index, this.props.defaultFilter));
			this.props.onApplyFilterRequested();
		} else if (this.props.indexTimestamp !== prevProps.indexTimestamp) {
			this.props.onApplyFilterRequested();
		}
	}

	componentWillUnmount() {
		this.ref.current.removeEventListener('action', this.onItemAction);
	}

	setIndex(index) {
		this.props.ctx.setRootProperty(this.props.indexPath, index);
	}

	setPage(firstRow, maxRowsPerPage) {
		const ctx = this.props.ctx;
		const path = this.props.indexPath.concat('pager');
		if (maxRowsPerPage) {
			setMaxRowsPerPagePreference(ctx.getFullPath(), maxRowsPerPage);
		}
		const pager = ctx.getRootProperty(path).set('firstRow', firstRow).set('maxRowsPerPage', maxRowsPerPage);
		this.props.ctx.setRootProperty(path, pager);
	}

	render() {
		//const definition = this.props.definition;
		const ctx = this.props.ctx;
		//
		const index = this.props.index || getEmptyIndex();
		let pager = index.get('pager');
		let firstRow = pager.get('firstRow');
		let maxRowsPerPage = pager.get('maxRowsPerPage');
		const rowIndexes = index.get('rowIndex').slice(firstRow - 1, firstRow - 1 + maxRowsPerPage);
		let rows = this.props.rows;
		let rowCount = rows.size;
		const items = this.props.items;
		//console.log('indexPath = ', this.props.indexPath);
		//console.log('index = ', index.toJS());
		/*if (!this.props.readOnly) {
			rows = rows.push(EMPTY_MAP);
		}*/
		return (
			<Container className="ps-tabularInput" marginTop={this.props.marginTop} margin={this.props.margin} ref={this.ref}>
				{
					this.props.withRowCountMessage && (
						<RowCountMessage
							pager={pager}
							minus={this.props.readOnly || this.props.noCreate ? 0 : 1}
							onPageRequested={this.setPage}
						/>
					)
				}
				<Table>
					<colgroup>
						{
							items.map((item, index) => {
								return (
									<Col key={index} width={item.getWidth() || '*'} minWidth={item.getMinWidth()}/>
								)
							})
						}
						<col width="12px"/>
					</colgroup>
					{this.props.noHeader ? null : (
						<Header
							items={items}
							index={index}
							indexPath={this.props.indexPath}
							ctx={ctx}
							mainHeader={this.props.mainHeader}
							onSortRequested={this.props.onSortRequested}
							onApplyFilterRequested={this.props.onApplyFilterRequested}
						/>
					)}
					<tbody>
						{
							rowIndexes.map((rowIndex, index) => {
								const row = rowIndex === rowCount ? EMPTY_MAP : rows.get(rowIndex);
								return (
									<Row
										ctx={ctx}
										row={row}
										items={items}
										key={rowIndex}
										index={rowIndex}
										errors={ctx.getError(rowIndex)}
										onRowDeleted={this.props.onRowDeleted}
										isDeletable={this.props.isDeletable}
										isNew={rowIndex === rowCount}
										isLast={index + 1 === rowIndexes.length}
										rowClassCallback={this.props.rowClassCallback}
									/>
								);
							})
						}
					</tbody>
				</Table>
				<Pager
					pager={pager}
					onPageRequested={this.setPage}
				/>
			</Container>
		);
	}
}
